import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, ChevronDown, Cpu, Mic2, Plus, Sparkles } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { BASE_MODELS, VOICE_STATUS } from "@/lib/sunoVoicesApi";

const DEFAULT_ENGINE = { model: "V6", custom_model_id: null };

function Item({ testId, active, disabled, icon: Icon, title, sub, onClick }) {
  return (
    <button type="button" data-testid={testId} aria-pressed={active} disabled={disabled} onClick={onClick}
      className={`flex w-full items-center gap-3 rounded-xl px-3 py-2 text-left transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-45 ${active ? "bg-white/[0.1]" : "hover:bg-white/[0.06]"}`}>
      <Icon className="h-4 w-4 shrink-0 text-white/60" />
      <span className="min-w-0 flex-1"><span className="block truncate text-[13px] font-semibold text-white">{title}</span><span className="block truncate text-[11px] text-white/45">{sub}</span></span>
      {active ? <Check className="h-4 w-4 text-cyan-300" /> : null}
    </button>
  );
}

const Heading = ({ children }) => <p className="px-3 pb-1 pt-3 font-mono text-[10px] uppercase tracking-[0.18em] text-white/40">{children}</p>;

/** The song's model menu (like Suno's): your ELPM voices first, then your older voices, then the V6 models. */
export default function ModelMenu({ engine, vocal, sunoVoices, oldVoices, onChange }) {
  const [open, setOpen] = useState(false);
  const eng = { ...DEFAULT_ENGINE, ...(engine || {}) };
  const suno = (sunoVoices || []).find((v) => v.id === eng.custom_model_id);
  const oldOn = !eng.custom_model_id && vocal.mode === "signature" && vocal.source === "my_voice";
  const old = oldOn ? (oldVoices || []).find((v) => v.voice_id === vocal.voice_id) : null;
  const base = BASE_MODELS.find((m) => m.id === eng.model) || BASE_MODELS[0];
  const pick = (patch) => { onChange(patch); setOpen(false); };
  const pickELPM = (v) => pick({ engine: { ...eng, custom_model_id: v.id }, vocal: { ...vocal, mode: "signature", source: "ai" } });
  const pickOld = (v) => pick({ engine: { ...eng, custom_model_id: null }, vocal: { ...vocal, mode: "signature", source: "my_voice", voice_id: v.voice_id } });
  const pickBase = (m) => pick({ engine: { model: m.id, custom_model_id: null }, ...(oldOn ? { vocal: { ...vocal, source: "ai" } } : {}) });
  const title = suno ? suno.name : oldOn ? (old?.name || "Your recorded voice") : base.label;
  const sub = suno ? `Your ELPM voice${suno.is_default ? " (default)" : ""} · ${base.label}` : oldOn ? `Older voice · ${base.label}` : "ELPM model · AI singer";
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="model-menu-trigger" className="flex w-full items-center gap-3 rounded-2xl border border-white/10 bg-black/40 px-4 py-3 text-left transition-colors hover:border-cyan-300/40">
          {suno ? <Sparkles className="h-4 w-4 text-cyan-300" /> : oldOn ? <Mic2 className="h-4 w-4 text-amber-200" /> : <Cpu className="h-4 w-4 text-white/60" />}
          <span className="min-w-0 flex-1"><span data-testid="model-menu-label" className="block truncate text-[14px] font-semibold">{title}</span><span className="block truncate text-[11px] text-white/45">{sub}</span></span>
          <ChevronDown className={`h-4 w-4 text-white/50 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </PopoverTrigger>
      <PopoverContent data-testid="model-menu" align="start" className="max-h-[70vh] w-[320px] overflow-y-auto border-white/10 bg-[#0b0e1c] p-1.5 text-white">
        <Heading>Your ELPM voices</Heading>
        {(sunoVoices || []).map((v) => (
          <Item key={v.id} testId={`model-menu-suno-voice-${v.id}`} active={suno?.id === v.id} disabled={v.status !== "ready"} icon={Sparkles} title={v.name}
            sub={v.status === "ready" ? `Sings in your real voice${v.is_default ? " · default" : ""}` : VOICE_STATUS[v.status]?.label || "Not ready"} onClick={() => pickELPM(v)} />
        ))}
        <Link to="/create/my-voices" data-testid="model-menu-new-voice" className="flex items-center gap-3 rounded-xl px-3 py-2 text-[13px] text-cyan-200 hover:bg-white/[0.06]">
          <Plus className="h-4 w-4" /> {sunoVoices?.length ? "New ELPM voice" : "Teach ELPM your voice"}
        </Link>
        {oldVoices?.length ? <Heading>Your older voices</Heading> : null}
        {(oldVoices || []).map((v) => <Item key={v.voice_id} testId={`model-menu-old-voice-${v.voice_id}`} active={oldOn && vocal.voice_id === v.voice_id} icon={Mic2} title={v.name} sub="Recorded voice · re-sings the AI vocal" onClick={() => pickOld(v)} />)}
        <Heading>ELPM models</Heading>
        {BASE_MODELS.map((m) => <Item key={m.id} testId={`model-menu-base-${m.id}`} active={!suno && !oldOn && eng.model === m.id} icon={Cpu} title={m.label} sub={m.hint} onClick={() => pickBase(m)} />)}
      </PopoverContent>
    </Popover>
  );
}
